import { useFormik } from "formik";
import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL, GET_CAR, PUT_CAR } from "../../../constant-value";
import { carSchema } from "../../../constant-value/schema";
import { IoChevronBackOutline } from "react-icons/io5";
import alphabet from "../../../constant-value/alphaet.json";
import { toast } from "react-toastify";
import api from "../../../services/axiosConfig";
import { motion } from "framer-motion";

// کامپوننت فیلد ورودی سفارشی
const FormField = ({ label, id, type = "text", formik, ...props }) => {
  const hasError = formik.errors[id] && formik.touched[id];
  
  return (
    <div className="mb-3 form-floating">
      <input
        id={id}
        name={id}
        type={type}
        value={formik.values[id] || ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        className={`form-control ${hasError ? "is-invalid" : ""}`}
        placeholder={label}
        {...props}
      />
      <label htmlFor={id}>{label}</label>
      {hasError && (
        <div className="invalid-feedback">{formik.errors[id]}</div>
      )}
    </div>
  );
};

// کامپوننت ورودی عددی
const NumberField = ({ label, id, formik }) => (
  <FormField
    label={label}
    id={id}
    type="text"
    inputMode="numeric"
    formik={formik}
  />
);

// کامپوننت پلاک
const PelakField = ({ formik }) => {
  const pelakFields = ["pelak1", "pelak2", "pelak3", "pelak4"];
  const hasPelakError = pelakFields.some(
    (field) => formik.errors[field] && formik.touched[field]
  );

  return (
    <div className="row mb-4">
      <div className="col-12">
        <label className="form-label mb-2">پلاک</label>
        <div className="d-flex flex-wrap justify-content-between">
          <div className="col-3 col-md-2 p-1">
            <input
              id="pelak4"
              name="pelak4"
              value={formik.values.pelak4 || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputMode="numeric"
              className={`form-control text-center ${
                formik.errors.pelak4 && formik.touched.pelak4 ? "is-invalid" : ""
              }`}
            />
          </div>
          <div className="col-3 col-md-2 p-1">
            <input
              id="pelak3"
              name="pelak3"
              value={formik.values.pelak3 || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputMode="numeric"
              className={`form-control text-center ${
                formik.errors.pelak3 && formik.touched.pelak3 ? "is-invalid" : ""
              }`}
            />
          </div>
          <div className="col-3 col-md-2 p-1">
            <select
              id="pelak2"
              name="pelak2"
              onChange={formik.handleChange}
              value={formik.values.pelak2 || ""}
              className="form-select text-center"
            >
              {alphabet.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-3 col-md-2 p-1">
            <input
              id="pelak1"
              name="pelak1"
              value={formik.values.pelak1 || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputMode="numeric"
              className={`form-control text-center ${
                formik.errors.pelak1 && formik.touched.pelak1 ? "is-invalid" : ""
              }`}
            />
          </div>
        </div>
        {hasPelakError && (
          <div className="invalid-feedback d-block">
            {formik.errors.pelak1 || formik.errors.pelak3 || formik.errors.pelak4}
          </div>
        )}
      </div>
    </div>
  );
};

// کامپوننت انتخاب تصویر
const ImageUploader = ({ selectedFile, setSelectedFile, setFieldValue, imageUrl }) => {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("فقط فایل تصویری مجاز است!");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error("حجم تصویر نباید بیشتر از 2 مگابایت باشد!");
        return;
      }
      setSelectedFile(file);
      setFieldValue("image", file);
    }
  };

  return (
    <div className="text-center mb-4">
      <img
        src={
          selectedFile
            ? URL.createObjectURL(selectedFile)
            : imageUrl || `${BASE_URL}/Images/Car/car.png`
        }
        alt="Car"
        className="rounded-circle shadow-sm mb-2"
        style={{ width: "120px", height: "120px", objectFit: "cover" }}
      />
      <div>
        <label className="btn btn-outline-primary btn-sm mt-2">
          انتخاب تصویر
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
        </label>
      </div>
    </div>
  );
};

const EditCar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);

  // بارگذاری دیتا
  useEffect(() => {
    api
      .get(`${BASE_URL}${GET_CAR}${id}`)
      .then((res) => {
        setCar({
          title: res.data.title,
          pelak1: res.data.pelak14Edit,
          pelak2: res.data.pelak2,
          pelak3: res.data.pelak34Edit,
          pelak4: res.data.pelak44Edit,
          shasiNo: res.data.shasiNo,
          productYear: res.data.productYear4Edit,
          kilometer: res.data.kilometer4Edit,
          useFuel: res.data.useFuel4Edit,
          company: res.data.company,
          distancePerWeek: res.data.distancePerWeek4Edit,
          userId: res.data.userId,
          imageUrl: res.data.imageUrl,
        });
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: car ? {
      ...car,
      useFuel: car.useFuel?.toString() || "",
      kilometer: car.kilometer?.toString() || "",
      distancePerWeek: car.distancePerWeek?.toString() || "",
      pelak1: car.pelak1?.toString() || "",
      pelak2: car.pelak2?.toString() || "",
      pelak3: car.pelak3?.toString() || "",
      pelak4: car.pelak4?.toString() || "",
    } : {},
    validationSchema: carSchema,
    onSubmit: (values, { setSubmitting }) => {
      const payload = {
        ...values,
        useFuel: parseFloat(values.useFuel),
        kilometer: parseFloat(values.kilometer),
        distancePerWeek: parseFloat(values.distancePerWeek),
        pelak1: parseInt(values.pelak1),
        pelak2: parseInt(values.pelak2),
        pelak3: parseInt(values.pelak3),
        pelak4: parseInt(values.pelak4),
      };

      const fd = new FormData();
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== undefined && payload[key] !== null) {
          fd.append(key, payload[key]);
        }
      });

      if (selectedFile) {
        fd.append("file", selectedFile);
      }

      api
        .put(`${PUT_CAR}${id}`, fd)
        .then((res) => {
          if (res.data.isSuccess) {
            toast.success(res.data.message, { autoClose: 1500 });
            navigate(-1);
          } else {
            toast.warning(res.data.message, { autoClose: 1500 });
          }
        })
        .catch((err) => {
          toast.error("خطا در ارتباط با سرور", { autoClose: 1500 });
          console.log(err);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  if (isLoading) return <div className="text-center mt-5">در حال بارگذاری...</div>;
  if (!car) return <div className="text-center mt-5">خودرویی یافت نشد</div>;

  return (
    <Fragment>
      <div className="container py-3">
        <div className="row mb-4">
          <div className="col-12 position-relative">
            <h2 className="text-center mb-4">ویرایش خودرو</h2>
            <div className="position-absolute top-0 end-0">
              <motion.button
                type="button"
                className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "36px", height: "36px" }}
                onClick={() => navigate(-1)}
                whileHover={{ scale: 1.15, rotate: -10 }}
                whileTap={{ scale: 0.9 }}
              >
                <IoChevronBackOutline size={20} />
              </motion.button>
            </div>
          </div>
        </div>

        <form className="card p-3" onSubmit={formik.handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <ImageUploader
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                setFieldValue={formik.setFieldValue}
                imageUrl={formik.values.imageUrl}
              />
              <FormField label="نام خودرو" id="title" formik={formik} />
              <FormField label="شرکت سازنده" id="company" formik={formik} />
              <NumberField label="سال ساخت" id="productYear" formik={formik} />
              <FormField label="شماره شاسی" id="shasiNo" formik={formik} />
            </div>

            <div className="col-md-6">
              <NumberField label="مصرف سوخت در 100 کیلومتر" id="useFuel" formik={formik} />
              <NumberField label="کیلومتر فعلی km" id="kilometer" formik={formik} />
              <NumberField label="متوسط حرکت (هفتگی) km" id="distancePerWeek" formik={formik} />
            </div>
          </div>

          <PelakField formik={formik} />

          <div className="d-grid mt-4">
            <button
              type="submit"
              disabled={formik.isSubmitting || !formik.dirty}
              className="btn btn-primary py-2"
            >
              {formik.isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  در حال ثبت...
                </>
              ) : (
                "ثبت اطلاعات"
              )}
            </button>
          </div>
        </form>
      </div>
    </Fragment>
  );
};

export default EditCar;